<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid, reset, errors}" ref="observer">
    <artisio-modal
          size="xl" :visible="modal.show" :title="$t('Move Item(s) to Another Receipt')" :ok-title="$t('Submit')"
          scrollable @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)">
      <spinner show v-if="modal.loading" height="20"/>
      <b-form v-else @submit.prevent="handleSubmit(onSubmit)">
        <div class="row">
          <div class="col-md-6">
            <receipt-select-input-widget :model="model" attribute="receipt_uuid" rules="required"/>
          </div>
          <div class="col-md-6">
            <input-widget :model="model" attribute="note" type="textarea"/>
          </div>
        </div>
      </b-form>
      <div class="row">
        <div class="col">{{ $t('Selected Items') + ': ' + modal.items.length }}</div>
      </div>
      <artisio-table :items="modal.items" :fields="tableFields">
        <template slot="cell(stock_no)" slot-scope="{item: inventory}">
          {{ inventory.stock_no }}
        </template>
        <template slot="cell(title)" slot-scope="{item: inventory}">
          {{ inventory.dynamic_fields | attributeByMasterLanguage('title') | striptags | truncate(50) }}
        </template>
        <template slot="cell(description)" slot-scope="{item: inventory}">
          {{ inventory.dynamic_fields | attributeByMasterLanguage('description') | striptags | truncate(50) }}
        </template>
        <template slot="cell(item_location)" slot-scope="{item: inventory}">
          {{ inventory.item_location ? inventory.item_location.name : '' }}
        </template>
        <template slot="cell(department)" slot-scope="{item: inventory}">
          {{ inventory.department.name | masterLanguage }}
        </template>
        <template slot="cell(category)" slot-scope="{item: inventory}">
          {{ inventory.category.name | masterLanguage }}
        </template>
        <template slot="cell(tax_status)" slot-scope="{item: inventory}">
          {{ inventory.tax_status ? inventory.tax_status.name : '' }}
        </template>
        <template slot="cell(tax_rate)" slot-scope="{item: inventory}">
          {{ inventory.tax_rate }}
        </template>
        <template slot="cell(commission_tiers)" slot-scope="{item: inventory}">
          <template v-for="commission in getCalculatedCommissions(inventory.commission_tiers)">
            {{ commission.percent | asPercentage }} - {{ commission.amount_over | asCurrency }}
            <br/>
          </template>
        </template>
      </artisio-table>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "@/core/components/artisio-modal/ArtisioModal";
  import Spinner from "@/core/components/common/Spinner";
  import InputWidget from "@/core/components/input-widget/InputWidget";
  import ArtisioTable from "@/core/components/ams-table/ArtisioTable";
  import BatchUpdateReceiptModel from "./BatchUpdateReceipt.model";
  import InventoryService from "@/modules/inventory/InventoryService";
  import ReceiptSelectInputWidget from "@/core/widgets/ReceiptSelectInputWidget";
  import helperService from "@/core/services/helper.service";
  import cloneDeep from "lodash/cloneDeep";
  import {createNamespacedHelpers} from "vuex";

  const {mapState, mapActions} = createNamespacedHelpers('inventory');

  export default {
    name: "BatchUpdateReceiptModal",
    components: {
      InputWidget,
      ArtisioModal,
      ArtisioTable,
      Spinner,
      ReceiptSelectInputWidget
    },
    data() {
      return {
        model: new BatchUpdateReceiptModel(),
        tableFields: [
          {key: 'stock_no', label: this.$t('Receipt / Item'), sortable: false},
          {key: 'title', label: this.$t('Title'), sortable: false},
          {key: 'description', label: this.$t('Description'), tdClass: 'description-col', sortable: false},
          {key: 'item_location', label: this.$t('Item Location'), sortable: false},
          {key: 'department', label: this.$t('Department'), sortable: false},
          {key: 'category', label: this.$t('Category'), sortable: false},
          {key: 'tax_status', label: this.$t('Tax Status'), sortable: false},
          {key: 'tax_rate', label: this.$t('Tax Rate'), sortable: false},
          {key: 'commission_tiers', label: this.$t('Commission'), sortable: false},
        ]
      }
    },
    computed: {
      ...mapState({
        modal: state => state.batchUpdateReceiptModal,
      }),
    },
    methods: {
      ...mapActions(['hideBatchUpdateReceiptModal']),
      getCalculatedCommissions(commissions) {
        if (!commissions || !commissions.length) return [];
        let data = helperService.reverseCalculateTiers({commissions: cloneDeep(commissions)});
        return data.commissions;
      },
      onHidden() {
        this.model = new BatchUpdateReceiptModel();
        this.hideBatchUpdateReceiptModal();
      },
      async onSubmit() {
        if (this.modal.loading) {
          return;
        }
        this.model.uuids = this.modal.items.map(item => item.uuid);
        this.modal.loading = true;
        let res = await InventoryService.batchUpdateReceipt(this.model);
        this.modal.loading = false;
        if (res.success) {
          this.hideBatchUpdateReceiptModal();
          this.$toast('success', this.$t('Processing started'));
        } else {
          if (res.body.non_field_errors) {
            this.$errorDialog(res.body.non_field_errors[0]);
          } else {
            this.$errorDialog(this.$t('Failed to move items to another receipt'));
          }
        }
      }
    },
  }
</script>

<style lang="scss" scoped>
  .artisio-table {
    /deep/ {
      .description-col {
        max-width: 500px;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
</style>
